import React from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';
import css from './SectionWhyUs.module.css';
import config from '../../../config/config';
import { t } from '../../../util/messages';

/**
 * Why gitribe.
 *
 * Two columns, per the approved design: the team photo stands alone on the
 * left, and the eyebrow, heading and three pillars all sit in the right
 * column. There is deliberately no supporting paragraph — the pillars carry
 * the argument.
 */

/**
 * Pillar icons, keyed by step id.
 *
 * Line icons rather than the `configWhyUs` step assets: those are the
 * three-tone geometric marks used for the delivery process, so reusing them
 * here made two different ideas look identical.
 */
const PILLAR_ICONS = {
  step1: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M2.8 20c0-3.4 2.8-5.6 6.2-5.6s6.2 2.2 6.2 5.6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <path
        d="M16.4 5.2a3.4 3.4 0 010 6.4M18.4 20c0-2.4-.9-4.2-2.4-5.3"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  ),
  step2: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3.4 17.6L9 11.4l3.8 3.4 7.2-8.4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.6 6.4H20v4.4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  step3: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20.4 12.6c0 4.4-3.6 7-8.4 8.6-4.8-1.6-8.4-4.2-8.4-8.6V6.2l8.4-3.2 8.4 3.2v6.4z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="M8.6 12l2.4 2.4L15.8 9.6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

const TeamIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="9" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.7" />
    <path
      d="M2.8 20c0-3.4 2.8-5.6 6.2-5.6s6.2 2.2 6.2 5.6"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M16.4 5.2a3.4 3.4 0 010 6.4M18.4 20c0-2.4-.9-4.2-2.4-5.3"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);

const SectionWhyUs = ({ rootClassName, className }) => {
  const classes = classNames(rootClassName || css.root, className);
  const { steps, sideImage } = config.configWhyUs;

  return (
    <section id="why-gitribe" className={classes} aria-label="Why choose Gitribe">
      <div className={css.content}>
        <div className={css.wrapper}>
          <figure className={css.image} data-reveal>
            <img
              className={css.rootForImage}
              src={sideImage.src}
              alt={sideImage.alt}
              width="1100"
              height="900"
              loading="lazy"
            />
            <figcaption className={css.imageBadge}>
              <span className={css.imageBadgeIcon} aria-hidden="true">
                <TeamIcon />
              </span>
              {t('SectionWhyUs.teamBadge')}
            </figcaption>
          </figure>

          <div className={css.body}>
            <span className={css.tag} data-reveal>
              <i aria-hidden="true" />
              {t('SectionWhyUs.titleHighlightMessage')}
            </span>
            <h2 className={css.title} data-reveal data-reveal-delay="70">
              {t('SectionWhyUs.heading')}
            </h2>

            <div className={css.steps}>
              {steps.map((step, index) => (
                <article
                  key={step.id}
                  className={css.step}
                  data-reveal
                  data-reveal-delay={(index + 1) * 80}
                >
                  <div className={css.icon}>{PILLAR_ICONS[step.id]}</div>
                  <div className={css.stepDetails}>
                    <h3 className={css.stepTitle}>{step.title}</h3>
                    <p className={css.stepDescription}>{step.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

SectionWhyUs.propTypes = {
  rootClassName: string,
  className: string,
};

export default React.memo(SectionWhyUs);
